import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import glossariesApi, {
  Glossary,
  CreateGlossaryRequest,
  UpdateGlossaryRequest,
} from '@/services/apis/glossaries';

// Query keys
export const glossaryKeys = {
  all: ['glossaries'] as const,
  lists: () => [...glossaryKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...glossaryKeys.lists(), filters] as const,
  details: () => [...glossaryKeys.all, 'detail'] as const,
  detail: (id: string) => [...glossaryKeys.details(), id] as const,
};

export interface GlossariesFilters {
  limit?: number;
  categoryId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  language?: string;
}

// Hook for fetching glossaries (used with async list)
export const useGlossaries = (filters: GlossariesFilters = {}) => {
  const {
    limit = 10,
    categoryId,
    search,
    sortBy = 'name',
    sortOrder = 'asc',
    language = 'en',
  } = filters;

  return useQuery({
    queryKey: glossaryKeys.list({ limit, categoryId, search, sortBy, sortOrder, language }),
    queryFn: async () => {
      const response = await glossariesApi.getGlossaries({
        limit,
        categoryId,
        search,
        sortBy,
        sortOrder,
        language,
      });
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useGlossary = ({ id, shouldSkip, language = 'en' }: { id: string; shouldSkip: boolean; language?: string }) => {
  return useQuery({
    queryKey: glossaryKeys.detail(id),
    queryFn: async () => {
      const response = await glossariesApi.getGlossary(id, language);
      return response.data!;
    },
    enabled: !!id && !shouldSkip,
  });
};

// Hooks for mutations
export const useCreateGlossary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (glossaryData: CreateGlossaryRequest) => glossariesApi.createGlossary(glossaryData),
    onSuccess: () => {
      // Invalidate and refetch glossaries list
      queryClient.invalidateQueries({ queryKey: glossaryKeys.lists() });
    },
  });
};

export const useUpdateGlossary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGlossaryRequest }) =>
      glossariesApi.updateGlossary(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific glossary and glossaries list
      queryClient.invalidateQueries({ queryKey: glossaryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: glossaryKeys.lists() });
    },
  });
};

export const useDeleteGlossary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => glossariesApi.deleteGlossary(id),
    onSuccess: () => {
      // Invalidate glossaries list
      queryClient.invalidateQueries({ queryKey: glossaryKeys.lists() });
    },
  });
};

export const useBulkDeleteGlossaries = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => glossariesApi.bulkDeleteGlossaries(ids),
    onSuccess: () => {
      // Invalidate glossaries list
      queryClient.invalidateQueries({ queryKey: glossaryKeys.lists() });
    },
  });
};

